import { useDataProvider, useNotify, useRedirect, useGetList } from 'react-admin';
import { Fragment, useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Divider, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, createFilterOptions, Tooltip,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon   from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon  from '@mui/icons-material/Close';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { useNavigate } from 'react-router-dom';
import authFetch from '../utils/authFetch';
import useUsdRate from '../utils/useUsdRate';
import { card, btn, inputSx, ORDER_STATUS, NAVY, typo, layout } from '../styles/ds';
import SupplierAllocationSelector from './SupplierAllocationSelector';
import { saveLineAllocation } from './saveLineAllocation';

const STATUS_CHOICES = Object.entries(ORDER_STATUS).map(([id, { label }]) => ({ id, label }));
const EMPTY_LINE     = { variant: null, quantity: 1, price_ils: '', price_usd: '', cost_ils: '', cost_usd: '', allocationValue: null };
const fmtIls = v => Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUsd = v => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const filter = createFilterOptions();

// crypto.randomUUID() needs a secure context (HTTPS or localhost) and isn't
// guaranteed to exist — fall back to a manual UUIDv4 so a plain-HTTP
// deployment doesn't break every save.
const genIdempotencyKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
      })
);

// ── Quick-create customer dialog ───────────────────────────────────────────────
const QuickCustomerDialog = ({ open, inputValue, onClose, onCreated }) => {
  const notify = useNotify();
  const dp     = useDataProvider();

  // Pre-split typed name into first/last
  const parts = (inputValue || '').trim().split(/\s+/);
  const [firstName, setFirstName] = useState(parts[0] || '');
  const [lastName,  setLastName]  = useState(parts.slice(1).join(' ') || '');
  const [phone,     setPhone]     = useState('');
  const [email,     setEmail]     = useState('');
  const [saving,    setSaving]    = useState(false);

  // Reset fields when dialog opens with new input
  useEffect(() => {
    if (open) {
      const p = (inputValue || '').trim().split(/\s+/);
      setFirstName(p[0] || '');
      setLastName(p.slice(1).join(' ') || '');
      setPhone('');
      setEmail('');
    }
  }, [open, inputValue]);

  const handleSave = async () => {
    if (!firstName.trim()) { notify('נא להזין שם פרטי', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const { data } = await dp.create('customers', {
        data: {
          first_name: firstName.trim(),
          last_name:  lastName.trim() || '',
          phone:      phone.trim()    || null,
          email:      email.trim()    || null,
        },
      });
      notify(`הלקוח "${data.first_name} ${data.last_name}" נוצר`, { type: 'success' });
      onCreated({ id: data.id, label: `${data.first_name} ${data.last_name}`.trim() });
      onClose();
    } catch (e) {
      console.error(e);
      notify(e?.body?.error || e?.message || 'שגיאה ביצירת הלקוח', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonAddIcon fontSize="small" />
          <span>לקוח חדש</span>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <Box sx={layout.formGrid}>
            <TextField label="שם פרטי *" value={firstName} onChange={e => setFirstName(e.target.value)}
              fullWidth sx={inputSx} autoFocus />
            <TextField label="שם משפחה"  value={lastName}  onChange={e => setLastName(e.target.value)}
              fullWidth sx={inputSx} />
          </Box>
          <TextField label="טלפון" value={phone} onChange={e => setPhone(e.target.value)}
            fullWidth sx={inputSx} inputProps={{ dir: 'ltr' }} />
          <TextField label="אימייל" value={email} onChange={e => setEmail(e.target.value)}
            fullWidth sx={inputSx} inputProps={{ dir: 'ltr' }} type="email" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          צור לקוח
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main OrderCreate ───────────────────────────────────────────────────────────
const OrderCreate = () => {
  const dataProvider = useDataProvider();
  const notify       = useNotify();
  const redirect     = useRedirect();
  const navigate     = useNavigate();

  const [customer,    setCustomer]    = useState(null);
  const [status,      setStatus]      = useState('pending');
  const [lines,       setLines]       = useState([{ ...EMPTY_LINE }]);
  const [saving,      setSaving]      = useState(false);
  const [usdRate, setUsdRate, saveUsdRate] = useUsdRate();
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [dialogInput, setDialogInput] = useState('');
  const [extraCustomers, setExtraCustomers] = useState([]); // newly created ones

  const { data: customers = [], isLoading: cusLoading } = useGetList('customers', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'last_name', order: 'ASC' },
  });

  const [variants, setVariants]     = useState([]);
  const [varLoading, setVarLoading] = useState(true);
  useEffect(() => {
    authFetch('/variants?_start=0&_end=9999&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => { setVariants(Array.isArray(d) ? d : []); setVarLoading(false); })
      .catch(() => setVarLoading(false));
  }, []);

  const customerOptions = useMemo(() => {
    const base = customers.map(c => ({
      id: c.id,
      label: `${c.first_name} ${c.last_name}`.trim(),
    }));
    // Merge newly created customers not yet in the cached list
    const baseIds = new Set(base.map(c => c.id));
    return [...base, ...extraCustomers.filter(c => !baseIds.has(c.id))];
  }, [customers, extraCustomers]);

  const variantOptions = useMemo(() =>
    variants.map(v => ({ id: v.id, label: v.name || `וריאנט #${v.id}`, price_ils: v.price_ils, price_usd: v.price_usd, cost_ils: v.cost_ils, cost_usd: v.cost_usd, unit: v.unit, isFinishedProduct: v.product_is_finished_product })),
    [variants]
  );

  const setLine = (idx, patch) =>
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const pickVariant = (idx, variantOpt) => {
    if (!variantOpt) { setLine(idx, { variant: null, price_ils: '', price_usd: '', cost_ils: '', cost_usd: '', allocationValue: null }); return; }
    // A different variant means its supplier sources are different too —
    // let the selector re-derive (and re-display) a fresh default.
    setLine(idx, { variant: variantOpt, price_ils: variantOpt.price_ils ?? '', price_usd: variantOpt.price_usd ?? '', cost_ils: variantOpt.cost_ils ?? '', cost_usd: variantOpt.cost_usd ?? '', allocationValue: null });
    // This customer's own last price for this product (from a completed
    // order) always wins over the product's current global price — the
    // global default above is only what a brand-new customer would see.
    if (customer) {
      authFetch(`/order_items/last-price?customer_id=${customer.id}&variant_id=${variantOpt.id}`)
        .then(r => r.json())
        .then(last => {
          if (!last) return;
          setLine(idx, {
            price_ils: last.price_at_purchase ?? '',
            price_usd: last.price_usd_at_purchase ?? '',
          });
        })
        .catch(() => {});
    }
  };

  const addLine    = () => setLines(ls => [...ls, { ...EMPTY_LINE }]);
  const removeLine = idx => setLines(ls => ls.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    let ils = 0, usd = 0;
    for (const l of lines) {
      const qty = Number(l.quantity) || 0;
      ils += qty * (Number(l.price_ils) || 0);
      usd += qty * (Number(l.price_usd) || 0);
    }
    return { ils, usd };
  }, [lines]);

  const handleSave = async () => {
    if (!customer) { notify('נא לבחור לקוח', { type: 'warning' }); return; }
    const validLines = lines.filter(l => l.variant && Number(l.quantity) > 0);
    if (!validLines.length) { notify('נא להוסיף לפחות פריט אחד', { type: 'warning' }); return; }

    setSaving(true);
    try {
      // Create as pending first — stock deduction happens on the status
      // transition, based on items, so items must exist before the status changes.
      const order = await dataProvider.create('orders', {
        data: { customer_id: customer.id, status: 'pending', total_price_ils: totals.ils, total_price_usd: totals.usd },
      });
      const orderId = order.data.id;
      await Promise.all(validLines.map(async l => {
        const created = await dataProvider.create('order_items', {
          data: { order_id: orderId, variant_id: l.variant.id, quantity: Number(l.quantity),
            price_at_purchase: Number(l.price_ils) || 0, price_usd_at_purchase: Number(l.price_usd) || 0 },
        });
        await saveLineAllocation(created.data.id, l.allocationValue);
      }));
      if (status !== 'pending') {
        // The order was just created as 'pending' (schema default) above —
        // that's the known-good expected_status for this transition.
        try {
          const statusResult = await dataProvider.update('orders', {
            id: orderId,
            data: {
              customer_id: customer.id, status, total_price_ils: totals.ils, total_price_usd: totals.usd,
              expected_status: 'pending',
              idempotency_key: genIdempotencyKey(),
            },
            previousData: order.data,
          });
          const autoFilledFor = statusResult?.data?.auto_filled_usd_price_for;
          if (autoFilledFor?.length) {
            notify(`מחיר ₪ מולא אוטומטית לפי שער $ נוכחי עבור: ${autoFilledFor.join(', ')}`, { type: 'info' });
          }
        } catch (e) {
          // The order and its items are already saved at this point — only
          // this specific status transition failed. Say so, rather than a
          // generic error that would make the user think nothing was saved.
          console.error(e);
          authFetch(`/orders/${orderId}/notify`, { method: 'POST' }).catch(() => {});
          notify('ההזמנה והפריטים נשמרו, אך עדכון הסטטוס נכשל — יש לערוך את ההזמנה ולהגדיר את הסטטוס ידנית', { type: 'warning' });
          redirect('edit', 'orders', orderId);
          return;
        }
      }
      authFetch(`/orders/${orderId}/notify`, { method: 'POST' }).catch(() => {});
      notify('ההזמנה נשמרה בהצלחה', { type: 'success' });
      redirect('edit', 'orders', orderId);
    } catch (e) {
      console.error(e);
      notify(e?.body?.error || e?.message || 'שגיאה בשמירה', { type: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <Box className="crm-page" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography {...typo.pageTitle}>הזמנה חדשה</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}
          sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>חזרה</Button>
      </Stack>

      {/* Customer + Status */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color="primary.main" mb={2}>פרטי הזמנה</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Customer autocomplete with quick-create */}
            <Autocomplete
              options={customerOptions}
              loading={cusLoading}
              value={customer}
              onChange={(_, v) => {
                if (v?.inputValue != null) {
                  // "Create new customer" option selected
                  setDialogInput(v.inputValue);
                  setDialogOpen(true);
                } else {
                  setCustomer(v);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const createLabel = inputValue.trim()
                  ? `צור לקוח חדש: "${inputValue}"`
                  : 'צור לקוח חדש +';
                filtered.push({ inputValue: inputValue.trim(), label: createLabel, id: '__create__' });
                return filtered;
              }}
              getOptionLabel={o => {
                if (typeof o === 'string') return o;
                if (o?.inputValue) return o.inputValue;
                return o?.label ?? '';
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.id === '__create__' ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: NAVY, fontWeight: 700 }}>
                      <PersonAddIcon fontSize="small" sx={{ color: NAVY }} />
                      <span>{option.label}</span>
                    </Stack>
                  ) : option.label}
                </li>
              )}
              renderInput={params => (
                <TextField {...params} label="לקוח" sx={inputSx}
                  InputProps={{ ...params.InputProps,
                    endAdornment: <>{cusLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }}
                />
              )}
              freeSolo={false}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
            />

            <FormControl fullWidth>
              <InputLabel sx={{ backgroundColor: '#fff', px: 0.5 }}>סטטוס</InputLabel>
              <Select value={status} onChange={e => setStatus(e.target.value)} sx={inputSx} label="סטטוס">
                {STATUS_CHOICES.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Order items */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color="primary.main">פריטי הזמנה</Typography>
            <Stack className="crm-filter-row" direction="row" spacing={1.5} alignItems="center">
              <TextField label="שער $" size="small" type="number" value={usdRate}
                onChange={e => setUsdRate(e.target.value)} onBlur={saveUsdRate}
                inputProps={{ min: 0, step: 0.01, style: { width: 60, textAlign: 'center' } }}
                sx={inputSx} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addLine} sx={btn.primary}>
                הוסף פריט
              </Button>
            </Stack>
          </Stack>

          <Box data-mobile-scroll sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>מוצר</TableCell>
                  <TableCell sx={{ width: 90 }}  align="center">כמות</TableCell>
                  <TableCell sx={{ width: 120 }} align="center">מחיר מכירה ₪</TableCell>
                  <TableCell sx={{ width: 120 }} align="center">מחיר מכירה $</TableCell>
                  <TableCell sx={{ width: 110, color: '#888' }} align="center">עלות ₪</TableCell>
                  <TableCell sx={{ width: 110, color: '#888' }} align="center">עלות $</TableCell>
                  <TableCell sx={{ width: 110 }} align="center">סה"כ ₪</TableCell>
                  <TableCell sx={{ width: 110 }} align="center">סה"כ $</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => {
                  const qty     = Number(line.quantity) || 0;
                  const lineIls = qty * (Number(line.price_ils) || 0);
                  const lineUsd = qty * (Number(line.price_usd) || 0);
                  return (
                    <Fragment key={idx}>
                    <TableRow sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#faf8f5' }}>
                      <TableCell sx={{ py: 1 }}>
                        <Autocomplete
                          options={variantOptions} loading={varLoading} value={line.variant}
                          onChange={(_, v) => pickVariant(idx, v)}
                          getOptionLabel={o => o?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          size="small"
                          renderInput={params => (
                            <TextField {...params} placeholder="חפש מוצר..." sx={{ ...inputSx, minWidth: 200 }} />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <TextField type="number" size="small" value={line.quantity}
                          onChange={e => setLine(idx, { quantity: e.target.value })}
                          inputProps={{ min: 1, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 70 }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <TextField type="number" size="small" value={line.price_ils}
                          onChange={e => setLine(idx, { price_ils: e.target.value })}
                          inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 100 }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                        <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                          <TextField type="number" size="small" value={line.price_usd}
                            onChange={e => setLine(idx, { price_usd: e.target.value })}
                            inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ ...inputSx, width: 90 }} />
                          {Number(line.price_usd) > 0 && Number(usdRate) > 0 && (
                            <Tooltip title={`עדכן מחיר ₪ לפי שער ${usdRate}`} arrow>
                              <IconButton size="small"
                                onClick={() => setLine(idx, { price_ils: Math.round(Number(line.price_usd) * Number(usdRate) * 100) / 100 })}>
                                <CurrencyExchangeIcon sx={{ fontSize: 16, color: '#c8a96e' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#888', fontSize: 13 }}>
                        {line.cost_ils !== '' && line.cost_ils != null ? `₪${fmtIls(line.cost_ils)}` : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#888', fontSize: 13 }}>
                        {line.cost_usd !== '' && line.cost_usd != null ? `$${fmtUsd(line.cost_usd)}` : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: NAVY }}>₪{fmtIls(lineIls)}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: NAVY }}>${fmtUsd(lineUsd)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => removeLine(idx)} aria-label="הסרת שורה"
                          disabled={lines.length === 1} sx={{ color: '#c62828' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {line.variant && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, border: 'none', borderRight: '3px solid #c8a96e', backgroundColor: 'rgba(13,29,62,0.018)' }}>
                          <Box px={2} py={1}>
                            <SupplierAllocationSelector
                              variantId={line.variant.id}
                              quantity={qty}
                              unit={line.variant.unit}
                              isFinishedProduct={line.variant.isFinishedProduct}
                              value={line.allocationValue}
                              onChange={v => setLine(idx, { allocationValue: v })}
                              onRequestProductionBatch={(suggestedQty) => navigate('/production_batches/create', {
                                state: {
                                  finished_variant_id: line.variant.id,
                                  finished_variant_name: line.variant.label,
                                  quantity: suggestedQty,
                                },
                              })}
                              onRequestPurchase={(suggestedQty) => navigate('/purchases/create', {
                                state: {
                                  variant_id: line.variant.id,
                                  variant_name: line.variant.label,
                                  quantity: suggestedQty,
                                },
                              })}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Stack className="crm-actions" direction="row" justifyContent="flex-end" spacing={3}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">סה"כ ₪</Typography>
              <Typography fontWeight={700} fontSize={20} color={NAVY}>₪{fmtIls(totals.ils)}</Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">סה"כ $</Typography>
              <Typography fontWeight={700} fontSize={20} color={NAVY}>${fmtUsd(totals.usd)}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Actions */}
      <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate(-1)}
          sx={{ borderColor: '#ccc', color: '#666', borderRadius: 2 }}>ביטול</Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ backgroundColor: NAVY, borderRadius: 2, px: 4, '&:hover': { backgroundColor: '#162d5a' } }}>
          שמור הזמנה
        </Button>
      </Stack>

      {/* Quick-create customer dialog */}
      <QuickCustomerDialog
        open={dialogOpen}
        inputValue={dialogInput}
        onClose={() => setDialogOpen(false)}
        onCreated={newCustomer => {
          setExtraCustomers(prev => [...prev, newCustomer]);
          setCustomer(newCustomer);
        }}
      />
    </Box>
  );
};

export default OrderCreate;
